import useTranslation from '@/utils/hooks/translation/useTranslation';

import { handleAiEditError } from '@/utils/aiCaption/handleAiEditErrors';
//utils
import { post } from '@/utils/client';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { AiActionType } from '@/utils/hooks/context/scheduler/types';

//type
import type { LanguageAuthorizedType } from '@/lib/aiCaption/languageSubmenu';
import type { ToneOfVoiceType } from '@/lib/aiCaption/toneOfVoiceSubmenu';
//apiEndpoint
import { SCHEDULER_AI_CAPTIONS } from '@/lib/routes/apiEndpoints';

type useAiEditActionsProps = {
    clearError: () => void;
    isProcessingAction: (action: AiActionType) => void;
    errorCallback: (error: string) => void;
    successCallback: (value: string) => void;
    captionValue?: string;
    profileId: SocialProfileType['id'];
};
export function useAiEditActions({
    clearError,
    isProcessingAction,
    errorCallback,
    successCallback,
    captionValue,
    profileId
}: useAiEditActionsProps) {
    const { t } = useTranslation('scheduler');

    const socialProfileId = profileId.toString();

    async function handleEnhanceCaption() {
        if (typeof clearError === 'function') {
            clearError();
        }
        try {
            isProcessingAction('enhancing');
            const result = await post(SCHEDULER_AI_CAPTIONS.replace('%SPID%', socialProfileId), {
                subject: captionValue,
                enhance_caption: true
            });
            //add decodeURI to fix the issue with the special characters like htmlentities
            successCallback(decodeURI(result.data[0]));
        } catch (e) {
            errorCallback(
                t(
                    handleAiEditError(
                        e.message,
                        'scheduler_funnel.ai_caption.generation_error.enhancing'
                    )
                )
            );
        } finally {
            isProcessingAction(null);
        }
    }

    async function handleTranslateCaption(languageCode: LanguageAuthorizedType) {
        if (typeof clearError === 'function') {
            clearError();
        }
        try {
            isProcessingAction('translating');
            const result = await post(SCHEDULER_AI_CAPTIONS.replace('%SPID%', socialProfileId), {
                subject: captionValue,
                language: languageCode,
                only_translate: true
            });
            //add decodeURI to fix the issue with the special characters like htmlentities
            successCallback(decodeURI(result.data[0]));
        } catch (e) {
            errorCallback(
                t(
                    handleAiEditError(
                        e.message,
                        'scheduler_funnel.ai_caption.generation_error.translating'
                    )
                )
            );
        } finally {
            isProcessingAction(null);
        }
    }
    async function handleToneOfVoiceCaption(toneOfVoice: ToneOfVoiceType) {
        if (typeof clearError === 'function') {
            clearError();
        }
        try {
            isProcessingAction('changing_tone');
            const result = await post(SCHEDULER_AI_CAPTIONS.replace('%SPID%', socialProfileId), {
                subject: captionValue,
                tone_of_voice: toneOfVoice,
                only_tone_of_voice: true
            });
            //add decodeURI to fix the issue with the special characters like htmlentities
            successCallback(decodeURI(result.data[0]));
        } catch (e) {
            errorCallback(
                t(
                    handleAiEditError(
                        e.message,
                        'scheduler_funnel.ai_caption.generation_error.changing_tone'
                    )
                )
            );
        } finally {
            isProcessingAction(null);
        }
    }
    async function handleHashtags() {
        if (typeof clearError === 'function') {
            clearError();
        }
        try {
            isProcessingAction('generating_hashtags');
            const result = await post(SCHEDULER_AI_CAPTIONS.replace('%SPID%', socialProfileId), {
                subject: captionValue,
                only_with_hashtags: true
            });
            //add decodeURI to fix the issue with the special characters like htmlentities
            successCallback(decodeURI(result.data[0]));
        } catch (e) {
            errorCallback(
                t(
                    handleAiEditError(
                        e.message,
                        'scheduler_funnel.ai_caption.generation_error.generating_hashtags'
                    )
                )
            );
        } finally {
            isProcessingAction(null);
        }
    }

    return {
        handleEnhanceCaption,
        handleTranslateCaption,
        handleToneOfVoiceCaption,
        handleHashtags
    };
}
